import React, { useState, useEffect, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Radio, MonitorPlay, Repeat, Search } from "lucide-react";
import {
  InputWithMediaInfo,
  SelectedMediaCard,
  GeneralPlayer,
  toast,
} from "..";
import { UnifiedDateTimePicker } from "../DateInput/UnifiedDateTimePicker";
import { MediaModel, GetMediaApi, InputWithMediaInfoRef } from "@/database";
import { useDebounce, secondsToHms } from "@/utils";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { RootState } from "@/store";
import { getSearchedListMedia } from "@/store/media/media.action";
import { setParentMediaList } from "@/store/media/media.slice";
import BorrowMetadataModal, { BorrowCreditMeta } from "./BorrowMetadataModal";
import { Alert } from "@/utils/ui/Alert/Alert";
import "./premiere.scss";

const PREMIERE_SCHEDULE_BUFFER_MS = 60 * 1000;

function formatPremiereStartTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Parent search: published + ready (playable). */
const premiereParentMediaListParams = {
  key: "parentid" as const,
  status: 5,
  state: 10,
  must_not: { "media_ref.rel": ["premiere", "embed"], content_type: 2 },
};

function mediaToSelectedCard(media: MediaModel) {
  return {
    label: media.id,
    value: (media as any).title ?? "",
    image:
      getThumbnailUrlUtil({
        media,
        id: media.images?.thumb || media.images?.portrait,
        source: media.source,
      }) || "",
    duration: secondsToHms(media.meta?.duration),
    thumb: !!generateBaseUrl(media),
  };
}

const mapState = (state: RootState) => ({
  parentMediaList: state.media.parentMediaList.list,
});

const mapDispatch = {
  getSearchedListMedia,
  setParentMediaList,
};

const premiereConnector = connect(mapState, mapDispatch);

export type PremierePlaybackMode = "live" | "vod" | "livevod";

export interface PremiereData {
  parentMediaId: string | null;
  sourceInput: string | null;
  /** Epoch ms: date + time from UnifiedDateTimePicker (`outputFormat="timestamp"`). */
  media_schedule: number;
  playbackMode: PremierePlaybackMode;
  loopCount?: number;
  syncedMetadata?: Record<string, any> | null;
  syncedCreditMeta?: BorrowCreditMeta | null;
}

interface PremiereSchedulePanelOwnProps {
  premiereData: PremiereData;
  hasVideo: boolean;
  onDataChange?: (data: Partial<PremiereData>) => void;
  isWizard?: boolean;
}

/**
 * Schedule/settings panel for the Premiere wizard stage.
 * - Full mode (no video): Slike ID/Title search + playback + schedule
 * - Compact mode (has video): playback + schedule
 */
const PremiereSchedulePanelInner: React.FC<
  PremiereSchedulePanelOwnProps & ConnectedProps<typeof premiereConnector>
> = ({
  premiereData,
  hasVideo,
  onDataChange,
  parentMediaList,
  getSearchedListMedia,
  setParentMediaList,
  isWizard,
}) => {
  const [playbackMode, setPlaybackMode] = useState<PremierePlaybackMode>(
    premiereData.playbackMode || "livevod"
  );
  const [mediaSchedule, setMediaSchedule] = useState(
    premiereData.media_schedule || 0
  );
  const [loopCount, setLoopCount] = useState(premiereData.loopCount || 1);

  const [inputSourceText, setInputSourceText] = useState("");
  const debouncedInputSourceText = useDebounce(inputSourceText, 200);
  const [isValidatingId, setIsValidatingId] = useState(false);
  const [lockedSourceId, setLockedSourceId] = useState<string>(
    premiereData.sourceInput || ""
  );
  const [mediaData, setMediaData] = useState<MediaModel | null>(null);
  const [selectedSourceVideo, setSelectedSourceVideo] = useState<any>(null);
  const [isSourceEdit, setIsSourceEdit] = useState(false);
  const parentMediaInputRef = useRef<InputWithMediaInfoRef>(null);

  /* ── Sync metadata state ── */
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  /** Set when schedule is auto-bumped; kept until source reset or user edits schedule. */
  const [premiereStartAlertAt, setPremiereStartAlertAt] = useState<
    number | null
  >(null);
  const scheduleBumpGuardRef = useRef<number | null>(null);

  const hasLoop = playbackMode === "live" || playbackMode === "livevod";

  const playbackModes: {
    value: PremierePlaybackMode;
    label: string;
    desc: string;
    Icon: React.ElementType;
  }[] = [
    {
      value: "livevod",
      label: "Live + Replay",
      desc: "Goes live as scheduled, then auto-replays for late viewers.",
      Icon: Repeat,
    },
    {
      value: "live",
      label: "Live Only",
      desc: "Scheduled live broadcast. No replay after end.",
      Icon: Radio,
    },
    {
      value: "vod",
      label: "Video Only",
      desc: "Plays as a normal on-demand video. Always available.",
      Icon: MonitorPlay,
    },
  ];

  const emitChange = (partial: Partial<PremiereData>) => {
    onDataChange?.({
      playbackMode,
      media_schedule: mediaSchedule,
      loopCount,
      ...partial,
      sourceInput: partial.sourceInput ?? lockedSourceId,
    });
  };

  const bumpScheduleIfPast = (): number | null => {
    const now = Date.now();
    if (mediaSchedule > 0 && mediaSchedule < now) {
      const adjusted = now + PREMIERE_SCHEDULE_BUFFER_MS;
      scheduleBumpGuardRef.current = adjusted;
      setMediaSchedule(adjusted);
      setPremiereStartAlertAt(adjusted);
      return adjusted;
    }
    return null;
  };

  const handlePlaybackChange = (mode: PremierePlaybackMode) => {
    setPlaybackMode(mode);
    emitChange({ playbackMode: mode });
  };

  const handleMediaScheduleChange = (date: number | null) => {
    const val = date || 0;

    // UnifiedDateTimePicker echoes onChange after value sync; ignore stale past times
    // and programmatic echoes right after auto-bump so the alert stays visible.
    const bumpGuard = scheduleBumpGuardRef.current;
    if (bumpGuard != null) {
      if (val > 0 && val < bumpGuard - 1000) {
        return;
      }
      scheduleBumpGuardRef.current = null;
    }

    setMediaSchedule(val);
    setPremiereStartAlertAt(null);
    emitChange({ media_schedule: val });
  };

  useEffect(() => {
    if (debouncedInputSourceText.length >= 3) {
      getSearchedListMedia({
        ...premiereParentMediaListParams,
        stitle: debouncedInputSourceText,
      });
    } else {
      setParentMediaList({
        data: { list: [], count: 0 },
        error: null,
        isLoading: false,
      });
    }
  }, [debouncedInputSourceText, getSearchedListMedia, setParentMediaList]);

  useEffect(() => {
    const sid = premiereData.sourceInput;
    if (!sid || hasVideo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await GetMediaApi({ id: sid });
        if (cancelled || res.status !== 200) return;
        if (
          res.data.source === "embed" ||
          res.data?.media_ref?.rel === "simulive" ||
          res.data?.media_ref?.rel === "chapter"
        ) {
          return;
        }
        setMediaData(res.data);
        setLockedSourceId(res.data.id);
        setSelectedSourceVideo(mediaToSelectedCard(res.data));
        setIsSourceEdit(false);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [premiereData.sourceInput, hasVideo]);

  useEffect(() => {
    if (hasVideo || premiereData.sourceInput) return;
    setMediaData(null);
    setLockedSourceId("");
    setSelectedSourceVideo(null);
    setIsSourceEdit(false);
    setSyncEnabled(false);
  }, [premiereData.sourceInput, hasVideo]);

  const handleResetId = () => {
    setMediaData(null);
    setSelectedSourceVideo(null);
    setIsSourceEdit(false);
    setSyncEnabled(false);
    setLockedSourceId("");
    setPremiereStartAlertAt(null);
    scheduleBumpGuardRef.current = null;
    emitChange({ parentMediaId: null, sourceInput: "", syncedMetadata: null });
  };

  const validateAndSetSource = async (mediaId: string) => {
    setIsValidatingId(true);
    try {
      const res = await GetMediaApi({ id: mediaId });
      if (
        res.status === 200 &&
        res.data.source !== "embed" &&
        res.data?.media_ref?.rel !== "simulive" &&
        res.data?.media_ref?.rel !== "chapter"
      ) {
        setMediaData(res.data);
        setLockedSourceId(res.data.id);
        setSelectedSourceVideo(mediaToSelectedCard(res.data));
        setIsSourceEdit(false);
        const adjustedSchedule = bumpScheduleIfPast();
        emitChange({
          parentMediaId: res.data.id,
          sourceInput: res.data.id,
          ...(adjustedSchedule != null
            ? { media_schedule: adjustedSchedule }
            : {}),
        });
      } else if (res.status === 200 && res.data.source === "embed") {
        toast.fail("Embed content cannot be used for Premiere.");
        handleResetId();
      } else if (
        res.status === 200 &&
        res.data?.media_ref?.rel === "simulive"
      ) {
        toast.fail("Simulive content cannot be used for Premiere.");
        handleResetId();
      } else {
        toast.fail("Could not load the selected source.");
        handleResetId();
      }
    } catch {
      toast.fail("Could not validate the source.");
      handleResetId();
    }
    setIsValidatingId(false);
  };

  const handleParentMediaChange = (newValue: any) => {
    if (!newValue?.label) {
      handleResetId();
      return;
    }
    void validateAndSetSource(String(newValue.label));
  };

  const handleOpenSourceMenu = () => {
    setTimeout(() => parentMediaInputRef.current?.openMenu(), 0);
  };

  const handleSyncToggle = (checked: boolean) => {
    setSyncEnabled(checked);
    if (!checked) {
      emitChange({ syncedMetadata: null });
    }
  };

  const sourceFieldValue =
    lockedSourceId || (premiereData.sourceInput ?? "") || "";

  /* ── Render ── */

  return (
    <div className="border rounded mt-3">
      {/* Header — matches Publish section */}
      <div className="d-flex flex-row justify-content-between align-items-center bgGrey">
        <div className="p-2 text-uppercase fw-medium text-primary">
          Premiere Schedule
        </div>
      </div>

      <div className="d-flex flex-column p-3 pb-2 gap-3">
        {/* ── Parent video source (only when no video) — match MediaDetailsForm ── */}
        {!hasVideo && (
          <div className="position-relative">
            {premiereStartAlertAt != null ? (
              <Alert
                variant="warning"
                className="py-2 d-flex align-items-center mb-2 position-relative"
              >
                Your Premiere will start at{" "}
                {formatPremiereStartTime(premiereStartAlertAt)}.
                <i
                  className="bi bi-caret-down-fill position-absolute f-20"
                  style={{ bottom: "-12px", left: "24px", color: "#ffe59c" }}
                  aria-hidden
                />
              </Alert>
            ) : null}
            {selectedSourceVideo && !isSourceEdit ? (
              <div className="mt-1 d-flex flex-column gap-1">
                <label className="form-label mb-1">SOURCE</label>
                <SelectedMediaCard
                  media={selectedSourceVideo}
                  type="pre"
                  onClose={handleResetId}
                  onEdit={() => {
                    setIsSourceEdit(true);
                    handleOpenSourceMenu();
                  }}
                  showEditButton={true}
                  showDeleteButton={true}
                  showPreviewButton={true}
                  cardStyle={{ width: "80px", minWidth: "80px" }}
                  style={{ width: "220px" }}
                  maxLength={20}
                />
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="syncMeta"
                    checked={syncEnabled}
                    onChange={(e) => {
                      handleSyncToggle(e.target.checked);
                      if (e.target.checked) setSyncModalOpen(true);
                    }}
                  />
                  <label className="form-check-label small" htmlFor="syncMeta">
                    Borrow metadata from source
                  </label>
                </div>
                {mediaData ? (
                  <div className="mt-2 rounded overflow-hidden border border-light-subtle">
                    <div className="ratio ratio-16x9">
                      <GeneralPlayer id={mediaData.id as string} autoPlay={3} />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="position-relative">
                <Search className="searchInput" size={18} />
                <InputWithMediaInfo
                  ref={parentMediaInputRef}
                  label="SOURCE"
                  value={sourceFieldValue}
                  handleChange={(newValue: any) =>
                    handleParentMediaChange(newValue)
                  }
                  options={parentMediaList}
                  onInputChangeCallback={(params) => {
                    setInputSourceText(params.keyword);
                    if (params.keyword.length === 0) {
                      setParentMediaList({
                        data: { list: [], count: 0 },
                        error: null,
                        isLoading: false,
                      });
                    }
                  }}
                  handleBlur={() => {}}
                  menuPlacement="bottom"
                  hideArrow={true}
                  touched={false}
                  initialStructuredData={selectedSourceVideo}
                  initialInputText={
                    isSourceEdit && selectedSourceVideo
                      ? String(selectedSourceVideo.value ?? "")
                      : inputSourceText
                  }
                  customStyling={{ padding: "0 40px 0 20px" }}
                  placeholder="Search Title, Id"
                  loader={true}
                  showRequired={false}
                  disabled={isValidatingId}
                  menuStyles={{ left: "auto", right: isWizard ? "-25%" : "" }}
                />
                {isValidatingId ? (
                  <div
                    className="position-absolute end-0 top-50 translate-middle-y me-3"
                    style={{ marginTop: "10px" }}
                  >
                    <i className="spinner-border spinner-border-sm text-primary" />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* ── Playback Mode ── */}
        <div>
          <label
            className="ms-2 text-muted mb-2"
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            PLAYBACK MODE
          </label>
          <div
            className="premiere-mode-row"
            role="radiogroup"
            aria-label="Playback mode"
          >
            {playbackModes.map(({ value, label, Icon }) => {
              const active = playbackMode === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`premiere-mode-chip${active ? " premiere-mode-chip--active" : ""}`}
                  onClick={() => handlePlaybackChange(value)}
                >
                  <Icon size={13} />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
          <p className="premiere-mode-hint" aria-live="polite">
            {playbackModes.find((m) => m.value === playbackMode)?.desc}
          </p>
        </div>

        {/* Loop count — smooth reveal when Live is selected */}
        <div
          className={`premiere-loop-wrap ${hasLoop ? "premiere-loop-wrap--visible" : ""}`}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              ×
            </span>
            <input
              type="number"
              className="form-control form-control-sm text-center"
              style={{ width: "56px", padding: "2px 4px" }}
              min={1}
              value={loopCount}
              onChange={(e) => {
                const val = Math.max(1, parseInt(e.target.value) || 1);
                setLoopCount(val);
                emitChange({ loopCount: val });
              }}
            />
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              live loops count
            </span>
          </div>
        </div>

        {/* ── Start Date / Time ── */}
        <div style={{ marginBottom: "-12px" }}>
          <div className="row">
            <UnifiedDateTimePicker
              mode="datetime"
              onChange={handleMediaScheduleChange}
              value={mediaSchedule}
              outputFormat="timestamp"
              fromSimulive={true}
              dateLabel=""
              timeLabel=""
              width="100%"
            />
          </div>
        </div>
      </div>

      {/* ── Borrow Metadata Modal ── */}
      {syncModalOpen && mediaData && (
        <BorrowMetadataModal
          isOpen={syncModalOpen}
          mediaData={mediaData}
          onClose={() => {
            setSyncModalOpen(false);
            setSyncEnabled(false);
            emitChange({ syncedMetadata: null });
          }}
          onApply={(synced, creditMeta) => {
            emitChange({
              syncedMetadata: synced,
              syncedCreditMeta: creditMeta,
            });
            setSyncModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default premiereConnector(PremiereSchedulePanelInner);
